import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { formatInstructions } from '../utils/formatInstructions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const YouTubeMealCreator = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mealData, setMealData] = useState(null);
  const navigate = useNavigate();

  // Parse the textarea content back into the instructions object format
  const parseInstructions = (text) => {
    if (!text.trim()) return {};
    
    const sections = text.split(/\n\s*\n/);
    const result = {};
    
    for (const section of sections) {
      const [sectionName, ...steps] = section.split('\n').filter(Boolean);
      if (!sectionName) continue;
      
      const sectionTitle = sectionName.replace(':', '').trim();
      result[sectionTitle] = steps
        .map(step => step.replace(/^\s*-\s*/, '').trim())
        .filter(step => step);
    }
    
    return result;
  };

  const extractVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const videoId = extractVideoId(videoUrl);
    
    if (!videoId) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }

    try {
      setIsLoading(true);
      console.log('Sending request to process YouTube video:', videoId);
      const response = await axios.post(`${BACKEND_URL}/api/meals/from-youtube`, {
        video_id: videoId
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Received response:', response.data);
      
      if (response.data.status === 'exists') {
        // Video already exists in the community
        console.log('Video already exists:', response.data);
        toast.info(response.data.message, {
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        
        // If we have the existing meal data, show it
        if (response.data.existing_meal) {
          setMealData(response.data.existing_meal);
        }
        return;
      } 
      
      if (response.data.status === 'success' && response.data.data) {
        // New meal created successfully
        console.log('Successfully processed video:', response.data.data);
        setMealData(response.data.data);
        toast.success('Meal information extracted successfully!');
      } else if (response.data) {
        // Fallback for legacy response format or direct data
        console.log('Using direct response data:', response.data);
        setMealData(response.data);
        toast.success('Meal information extracted successfully!');
      } else {
        throw new Error('Invalid response format from server');
      }
    } catch (error) {
      console.error('Error extracting meal info:', error);
      
      let errorMessage = 'Failed to extract meal information from video';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const saveMeal = async () => {
    try {
      // First, validate required fields
      const requiredFields = ['name', 'calories', 'protein', 'carbs', 'fat'];
      const missingFields = requiredFields.filter(field => !mealData[field] && mealData[field] !== 0);
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
      }

      // Ensure all required fields are included and properly formatted
      const mealToSave = {
        name: String(mealData.name || '').trim(),
        calories: Number(mealData.calories || 0),
        protein: Number(mealData.protein || 0),
        carbs: Number(mealData.carbs || 0),
        fat: Number(mealData.fat || 0),
        category: String(mealData.category || 'main_course').trim(),
        meal_type: String(mealData.meal_type || 'lunch').trim(),
        type: String(mealData.type || 'veg').trim(),
        region: String(mealData.region || 'All').trim(),
        // Include video_id if available
        video_id: mealData.video_id || null,
        // All YouTube meals are shared with the community
        is_public: true,
        is_community: true,
        // Ensure ingredients is an array of strings
        ingredients: Array.isArray(mealData.ingredients) 
          ? mealData.ingredients.map(i => String(i).trim()).filter(i => i)
          : (mealData.ingredients ? [String(mealData.ingredients).trim()] : []),
        // Ensure instructions is a string
        instructions: typeof mealData.instructions === 'string' 
          ? mealData.instructions.trim()
          : (mealData.instructions ? JSON.stringify(mealData.instructions) : '')
      };
      
      // Additional validation
      if (!mealToSave.name) {
        throw new Error('Meal name is required');
      }
      
      if (mealToSave.ingredients.length === 0) {
        throw new Error('At least one ingredient is required');
      }

      console.log('Saving meal data:', mealToSave);
      const response = await axios.post(`${BACKEND_URL}/api/meals/custom`, mealToSave);
      console.log('Save meal response:', response.data);
      toast.success('Meal saved successfully!');
      navigate('/custom-meals');
    } catch (error) {
      console.error('Error saving meal:', error);
      
      let errorMessage = 'Failed to save meal';
      
      if (error.response) {
        console.error('Error response status:', error.response.status);
        console.error('Error response data:', error.response.data);
        
        // Handle validation errors (422)
        if (error.response.status === 422) {
          const details = error.response.data.detail;
          if (Array.isArray(details)) {
            errorMessage = details.map(err => {
              const field = err.loc && err.loc.length > 0 ? err.loc[err.loc.length - 1] : 'field';
              return `${field}: ${err.msg || 'Invalid value'}`;
            }).join('\n');
          } else if (typeof details === 'string') {
            errorMessage = details;
          } else if (typeof details === 'object') {
            errorMessage = JSON.stringify(details);
          }
        } 
        // Handle other error responses
        else if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else {
            errorMessage = JSON.stringify(error.response.data);
          }
        }
      } 
      // Handle network errors
      else if (error.request) {
        console.error('No response received:', error.request);
        errorMessage = 'No response from server. Please check your internet connection.';
      } 
      // Handle other errors
      else if (error.message) {
        errorMessage = error.message;
      }
      
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Show the error message in the UI
      toast.error(`Error: ${errorMessage}`, {
        autoClose: 5000, // Show for 5 seconds
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Create Meal from YouTube Video</h1>
          
          <form onSubmit={handleSubmit} className="mb-8">
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Enter a YouTube cooking video URL to automatically extract the recipe
              </p>
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                {isLoading ? 'Processing...' : 'Extract Meal Info'}
              </button>
            </div>
          </form>

          {mealData && (
            <div className="mt-8 border-t pt-6">
              <h2 className="text-xl font-semibold mb-4">Extracted Meal Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="aspect-w-16 aspect-h-9 mb-4">
                    <iframe
                      src={`https://www.youtube.com/embed/${extractVideoId(videoUrl)}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="w-full h-64 rounded-lg"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Meal Name</label>
                      <input
                        type="text"
                        value={mealData.name || ''}
                        onChange={(e) => setMealData({...mealData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        placeholder="Enter meal name"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Calories</label>
                        <input
                          type="number"
                          value={mealData.calories || ''}
                          onChange={(e) => setMealData({...mealData, calories: Number(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Calories"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Protein (g)</label>
                        <input
                          type="number"
                          value={mealData.protein || ''}
                          onChange={(e) => setMealData({...mealData, protein: Number(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Protein"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Carbs (g)</label>
                        <input
                          type="number"
                          value={mealData.carbs || ''}
                          onChange={(e) => setMealData({...mealData, carbs: Number(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Carbs"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fat (g)</label>
                        <input
                          type="number"
                          value={mealData.fat || ''}
                          onChange={(e) => setMealData({...mealData, fat: Number(e.target.value)})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          placeholder="Fat"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="mt-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
                    This meal will be shared with the community.
                  </div>
                    
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ingredients</label>
                    <textarea
                      value={Array.isArray(mealData.ingredients) ? mealData.ingredients.join('\n') : ''}
                      onChange={(e) => setMealData({
                        ...mealData,
                        ingredients: e.target.value.split('\n').filter(i => i.trim())
                      })}
                      className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Enter ingredients, one per line"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                    <div className="space-y-4">
                      {mealData.instructions && typeof mealData.instructions === 'object' ? (
                        <div className="space-y-6">
                          {Object.entries(mealData.instructions).map(([section, steps], idx) => {
                            // Ensure steps is an array
                            const stepList = Array.isArray(steps) ? steps : [steps];
                            
                            return (
                              <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                                <h4 className="font-semibold text-lg text-gray-800 mb-3 pb-2 border-b border-gray-100">
                                  {section}
                                </h4>
                                <ol className="list-decimal pl-5 space-y-2">
                                  {stepList.filter(step => step).map((step, stepIdx) => (
                                    <li key={stepIdx} className="text-gray-700">
                                      <span className="text-gray-600">{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-gray-500 italic p-4 bg-gray-50 rounded">
                          No instructions available. Add instructions in the editor below.
                        </div>
                      )}
                      
                      <div className="mt-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Edit Instructions</h4>
                        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                          <div className="text-xs text-gray-500 mb-2">
                            Format: Each section on a new line, steps with bullet points
                          </div>
                          <textarea
                            value={formatInstructions(mealData.instructions) || ''}
                            onChange={(e) => setMealData({
                              ...mealData,
                              instructions: parseInstructions(e.target.value)
                            })}
                            className="w-full h-48 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                            placeholder="Section Name:\n- Step 1\n- Step 2\n\nAnother Section:\n- First step\n- Second step"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      onClick={saveMeal}
                      className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Save Meal
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default YouTubeMealCreator;
